/**
 * Task API service.
 */
import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString, type PaginationParams } from './client';
import type {
  Task,
  CreateTaskDto,
  UpdateTaskDto,
  TaskPriority,
  TaskStatus,
  AssignableUser,
  TaskSummary,
} from '../types/tasks';

const BASE = '/api/tasks';

export interface TaskListParams extends PaginationParams {
  search?: string;
  priority?: TaskPriority;
  status?: TaskStatus;
  label?: string;
  mine?: boolean;
  includeClosed?: boolean;
}

export interface PaginatedTaskList {
  items: Task[];
  pagination: PaginationMetadata | null;
}

/**
 * `buildQueryString` only emits page/limit/sortBy/sortOrder flat and nests anything
 * else under `filter[...]`; the list endpoint validates its filters flat on
 * `req.query`, so those are appended here rather than silently dropped.
 */
const FILTER_KEYS = ['search', 'priority', 'status', 'label', 'mine', 'includeClosed'] as const;

const buildTaskQuery = (params: TaskListParams = {}): string => {
  const query = buildQueryString({
    page: params.page,
    limit: params.limit,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  });

  const filters = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const value = params[key];
    if (value !== undefined && value !== '') filters.append(key, String(value));
  }

  const suffix = filters.toString();
  if (!suffix) return query;
  return query ? `${query}&${suffix}` : `?${suffix}`;
};

/**
 * Ensure a task always has the array/boolean fields the UI expects. Tasks created
 * before labels/comments/activity/closed existed won't have them in storage.
 */
const normalize = (t: Task): Task => ({
  ...t,
  labels: t.labels ?? [],
  comments: t.comments ?? [],
  activity: t.activity ?? [],
  viewerUserIds: t.viewerUserIds ?? [],
  closed: t.closed ?? false,
});

/** Identifies the business record a Tasks rail is showing. */
export interface EntityTaskScope {
  pluginId: string;
  entityType: string;
  entityId: string;
}

export const tasksService = {
  /** KPI counts for the board. Counted server-side, since `list` is paginated. */
  summary: (): Promise<TaskSummary> => apiFetch<TaskSummary>(`${BASE}/summary`),

  list: async (params?: TaskListParams): Promise<PaginatedTaskList> => {
    const response = await apiFetch<PaginatedTaskList>(`${BASE}${buildTaskQuery(params)}`);
    return {
      items: (response?.items ?? []).map(normalize),
      pagination: response?.pagination ?? null,
    };
  },

  /** Tasks linked to one record. Returns [] if the caller can't see that record. */
  listForEntity: async (scope: EntityTaskScope): Promise<Task[]> => {
    const query = new URLSearchParams({
      pluginId: scope.pluginId,
      entityType: scope.entityType,
      entityId: scope.entityId,
    });
    const response = await apiFetch<Task[]>(`${BASE}/for-entity?${query.toString()}`);
    return (response ?? []).map(normalize);
  },

  getAssignableUsers: (searchQuery?: string): Promise<AssignableUser[]> => {
    const query = searchQuery
      ? `?${new URLSearchParams({ searchQuery }).toString()}`
      : '';
    return apiFetch<AssignableUser[]>(`${BASE}/assignable-users${query}`);
  },

  getById: async (id: string): Promise<Task> => normalize(await apiFetch<Task>(`${BASE}/${id}`)),

  create: async (data: CreateTaskDto): Promise<Task> =>
    normalize(await apiFetch<Task>(BASE, { method: 'POST', body: JSON.stringify(data) })),

  update: async (id: string, data: UpdateTaskDto): Promise<Task> =>
    normalize(await apiFetch<Task>(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(data) })),

  updateStatus: async (id: string, status: TaskStatus): Promise<Task> =>
    normalize(
      await apiFetch<Task>(`${BASE}/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
    ),

  addComment: async (id: string, body: string): Promise<Task> =>
    normalize(
      await apiFetch<Task>(`${BASE}/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      })
    ),

  close: async (id: string): Promise<Task> =>
    normalize(await apiFetch<Task>(`${BASE}/${id}/close`, { method: 'POST' })),

  reopen: async (id: string): Promise<Task> =>
    normalize(await apiFetch<Task>(`${BASE}/${id}/reopen`, { method: 'POST' })),

  remove: (id: string): Promise<{ id: string }> =>
    apiFetch<{ id: string }>(`${BASE}/${id}`, { method: 'DELETE' }),
};
